"use client";

import { useState } from "react";
import { flushSync } from "react-dom";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SMALL,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  answerLetter,
  arrangeQuiz,
  countCorrect,
  optionLetter as letter,
  MAX_QUIZ_OPTIONS,
  parseQuiz,
  type QuizError,
  type QuizShuffle,
  quizAsText,
  type ShownQuestion as Shown,
  shuffleQuiz,
} from "@/lib/tool-kit/classroom/quiz";
import { secureRandom } from "@/lib/tool-kit/classroom/random";

const SHEET_H2 = "font-semibold text-xl";

/** English strings. A translated content file overrides these through `ui`. */
export const MULTIPLE_CHOICE_QUIZ_MAKER_STRINGS = {
  quizLabel: "Questions — a question line, then one option per line",
  quizHint:
    "Start options with a letter and ) or . — such as a) or B. — or with a dash, and put * in front of the correct one. Leave a blank line between questions.",
  quizPlaceholder:
    "1. Which organelle releases energy from glucose?\na) Nucleus\n*b) Mitochondrion\nc) Ribosome\nd) Chloroplast",
  example:
    "1. What is the main source of energy for the water cycle?\na) Wind\n*b) The sun\nc) Earth's core\nd) The moon\n\n2. What is it called when water vapor cools into droplets?\na) Evaporation\n*b) Condensation\nc) Precipitation\nd) Infiltration\n\n3. Which process moves water from plant leaves into the air?\n*a) Transpiration\nb) Runoff\nc) Condensation\nd) Collection\n\n4. Rain, snow, sleet and hail are all forms of what?\na) Evaporation\nb) Collection\n*c) Precipitation\nd) Transpiration\n\n5. Where is most of Earth's water found?\na) Glaciers and ice caps\nb) Rivers and lakes\n*c) Oceans\nd) Groundwater",
  loadExample: "Load example",
  questionCount: "one:{count} question ready|other:{count} questions ready",
  errorOptionBeforeQuestion:
    "Line {line} looks like an answer option, but no question comes before it.",
  errorNoOptions:
    "Question {question} (line {line}) has no options. Put each option on its own line, starting with a letter such as a) or b).",
  errorOneOption:
    "Question {question} (line {line}) has only one option. Add at least one more.",
  errorTooManyOptions:
    "Question {question} (line {line}) has more than {max} options.",
  errorEmptyOption: "Line {line} has an option letter but no text after it.",
  errorNoCorrect:
    "Question {question} (line {line}) has no correct answer. Put * in front of the right option, like *b).",
  errorSeveralCorrect:
    "Question {question} (line {line}) marks more than one option as correct. Mark exactly one.",
  skippedNote: "Questions with problems are left out until they are fixed.",
  view: "View",
  viewPrint: "Printable quiz",
  viewOnline: "Take it online",
  titleLabel: "Quiz title",
  titlePlaceholder: "The Water Cycle",
  defaultTitle: "Quiz",
  instructionsLabel: "Instructions",
  defaultInstructions: "Circle the letter of the best answer.",
  options: "Options",
  showNameLine: "Name and date line",
  shuffleOptions: "Shuffle answer options",
  shuffleQuestions: "Shuffle question order",
  pinnedNote:
    'Options that mention "above" or point to other letters (such as "A and B") keep their place.',
  reshuffle: "Reshuffle",
  staleShuffle:
    "The questions changed since the last shuffle. Click Reshuffle.",
  printQuiz: "Print quiz",
  printKey: "Print answer key",
  showKey: "Show answer key",
  hideKey: "Hide answer key",
  copyText: "Copy quiz as text",
  copied: "Copied!",
  answerKeyTitle: "{title} — Answer key",
  name: "Name",
  date: "Date",
  checkAnswers: "Check answers",
  tryAgain: "Try again",
  scoreLine: "{correct} of {total} correct",
  correct: "Correct",
  incorrect: "Not quite — the answer is {answer}",
  unanswered: "Not answered — the answer is {answer}",
} as const;

type Strings = typeof MULTIPLE_CHOICE_QUIZ_MAKER_STRINGS;
type View = "print" | "online";
type PrintTarget = "quiz" | "key";

const ERROR_KEYS: Record<QuizError["kind"], keyof Strings> = {
  "option-before-question": "errorOptionBeforeQuestion",
  "no-options": "errorNoOptions",
  "one-option": "errorOneOption",
  "too-many-options": "errorTooManyOptions",
  "empty-option": "errorEmptyOption",
  "no-correct": "errorNoCorrect",
  "several-correct": "errorSeveralCorrect",
};

function PrintableQuiz({
  shown,
  title,
  instructions,
  nameLine,
  strings,
  className,
}: {
  shown: Shown[];
  title: string;
  instructions: string;
  nameLine: boolean;
  strings: Strings;
  className: string;
}) {
  return (
    <section className={className}>
      <h2 className={SHEET_H2}>{title}</h2>
      {nameLine ? (
        <div className="flex flex-wrap gap-x-10 gap-y-3 text-sm">
          <span className="flex items-end gap-2">
            {strings.name}
            <span className="inline-block w-56 border-current border-b" />
          </span>
          <span className="flex items-end gap-2">
            {strings.date}
            <span className="inline-block w-32 border-current border-b" />
          </span>
        </div>
      ) : null}
      <p className="text-sm italic">{instructions}</p>
      <ol className="space-y-5">
        {shown.map((item, number) => (
          <li className="break-inside-avoid space-y-2" key={item.index}>
            <p className="font-medium text-sm">
              {number + 1}. {item.question.stem}
            </p>
            <ul className="space-y-1 ps-6 text-sm">
              {item.order.map((source, position) => (
                <li key={source}>
                  {letter(position)}. {item.question.options[source]}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </section>
  );
}

function OnlineQuiz({
  shown,
  strings,
  chosen,
  checked,
  onChoose,
}: {
  shown: Shown[];
  strings: Strings;
  chosen: Record<number, number>;
  checked: boolean;
  onChoose: (index: number, position: number) => void;
}) {
  return (
    <ol className="space-y-5">
      {shown.map((item, number) => {
        const pick = chosen[item.index];
        const right = item.order.indexOf(item.question.correct);
        const answer = `${letter(right)}. ${item.question.options[item.question.correct]}`;
        let feedback: string = strings.correct;
        if (pick === undefined) {
          feedback = t(strings.unanswered, { answer });
        } else if (pick !== right) {
          feedback = t(strings.incorrect, { answer });
        }
        return (
          <li key={item.index}>
            <fieldset className="space-y-2 rounded-lg border border-border bg-card p-4">
              <legend className="px-1 font-medium text-foreground text-sm">
                {number + 1}. {item.question.stem}
              </legend>
              {item.order.map((source, position) => (
                <label
                  className="flex cursor-pointer items-start gap-2 text-foreground text-sm"
                  key={source}
                >
                  <input
                    checked={pick === position}
                    className="mt-1 accent-primary"
                    disabled={checked}
                    name={`mcq-${item.index}`}
                    onChange={() => onChoose(item.index, position)}
                    type="radio"
                  />
                  <span>
                    {letter(position)}. {item.question.options[source]}
                  </span>
                </label>
              ))}
              {checked ? (
                <p
                  className={
                    pick === right
                      ? "font-medium text-primary text-sm"
                      : "font-medium text-destructive text-sm"
                  }
                >
                  {feedback}
                </p>
              ) : null}
            </fieldset>
          </li>
        );
      })}
    </ol>
  );
}

function errorMessage(error: QuizError, strings: Strings): string {
  return t(strings[ERROR_KEYS[error.kind]], {
    question: error.question,
    line: error.line,
    max: MAX_QUIZ_OPTIONS,
  });
}

function AnswerKey({
  shown,
  title,
  strings,
  className,
}: {
  shown: Shown[];
  title: string;
  strings: Strings;
  className: string;
}) {
  return (
    <section className={className}>
      <h2 className={SHEET_H2}>{t(strings.answerKeyTitle, { title })}</h2>
      <ol className="grid grid-cols-3 gap-x-6 gap-y-1 text-sm sm:grid-cols-5 print:grid-cols-5">
        {shown.map((item, number) => (
          <li key={item.index}>
            {number + 1}. {answerLetter(item)}
          </li>
        ))}
      </ol>
    </section>
  );
}

function SelfCheck({ shown, strings }: { shown: Shown[]; strings: Strings }) {
  const fmt = useToolNumber();
  const [chosen, setChosen] = useState<Record<number, number>>({});
  const [checked, setChecked] = useState(false);
  const score = countCorrect(
    shown.map((item) => item.question),
    shown.map((item) => item.order),
    shown.map((item) => chosen[item.index] ?? null)
  );
  return (
    <div className="space-y-5 print:hidden">
      <OnlineQuiz
        checked={checked}
        chosen={chosen}
        onChoose={(index, position) =>
          setChosen((previous) => ({ ...previous, [index]: position }))
        }
        shown={shown}
        strings={strings}
      />
      <div aria-live="polite">
        {checked ? (
          <StatCard
            emphasis
            label={t(strings.scoreLine, {
              correct: fmt(score),
              total: fmt(shown.length),
            })}
            value={`${fmt((score / shown.length) * 100, { maximumFractionDigits: 0 })}%`}
          />
        ) : null}
      </div>
      <button
        className={checked ? BUTTON_SECONDARY : BUTTON_PRIMARY}
        onClick={() => {
          if (checked) {
            setChosen({});
          }
          setChecked(!checked);
        }}
        type="button"
      >
        {checked ? strings.tryAgain : strings.checkAnswers}
      </button>
    </div>
  );
}

function Actions({
  strings,
  canShuffle,
  showKey,
  copyText,
  onReshuffle,
  onPrint,
  onToggleKey,
}: {
  strings: Strings;
  canShuffle: boolean;
  showKey: boolean;
  copyText: string;
  onReshuffle: () => void;
  onPrint: (target: PrintTarget) => void;
  onToggleKey: () => void;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {canShuffle ? (
        <button
          className={BUTTON_SECONDARY}
          onClick={onReshuffle}
          type="button"
        >
          {strings.reshuffle}
        </button>
      ) : null}
      <button
        className={BUTTON_PRIMARY}
        onClick={() => onPrint("quiz")}
        type="button"
      >
        {strings.printQuiz}
      </button>
      <button
        className={BUTTON_SECONDARY}
        onClick={() => onPrint("key")}
        type="button"
      >
        {strings.printKey}
      </button>
      <button
        aria-pressed={showKey}
        className={BUTTON_SECONDARY}
        onClick={onToggleKey}
        type="button"
      >
        {showKey ? strings.hideKey : strings.showKey}
      </button>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyText}
        text={copyText}
      />
    </div>
  );
}

function ParseErrors({
  errors,
  strings,
}: {
  errors: QuizError[];
  strings: Strings;
}) {
  if (errors.length === 0) {
    return null;
  }
  return (
    <ErrorNotice>
      {errors.map((error) => (
        <span className="block" key={`${error.kind}-${error.line}`}>
          {errorMessage(error, strings)}
        </span>
      ))}
      <span className="mt-2 block">{strings.skippedNote}</span>
    </ErrorNotice>
  );
}

interface Settings {
  title: string;
  instructions: string;
  nameLine: boolean;
  mixOptions: boolean;
  mixQuestions: boolean;
  view: View;
}

function SettingsPanel({
  settings,
  strings,
  onChange,
  onMix,
}: {
  settings: Settings;
  strings: Strings;
  onChange: (patch: Partial<Settings>) => void;
  /** A shuffle option was switched on or off. */
  onMix: (patch: Partial<Settings>, on: boolean) => void;
}) {
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="mcq-title"
          label={strings.titleLabel}
          onChange={(title) => onChange({ title })}
          placeholder={strings.titlePlaceholder}
          value={settings.title}
        />
        <TextField
          id="mcq-instructions"
          label={strings.instructionsLabel}
          onChange={(instructions) => onChange({ instructions })}
          placeholder={strings.defaultInstructions}
          value={settings.instructions}
        />
      </div>
      <fieldset className="space-y-2">
        <legend className="font-medium text-foreground text-sm">
          {strings.options}
        </legend>
        <Checkbox
          checked={settings.nameLine}
          label={strings.showNameLine}
          onChange={(nameLine) => onChange({ nameLine })}
        />
        <Checkbox
          checked={settings.mixOptions}
          label={strings.shuffleOptions}
          onChange={(mixOptions) => onMix({ mixOptions }, mixOptions)}
        />
        <Checkbox
          checked={settings.mixQuestions}
          label={strings.shuffleQuestions}
          onChange={(mixQuestions) => onMix({ mixQuestions }, mixQuestions)}
        />
        {settings.mixOptions ? (
          <p className="text-muted-foreground text-xs">{strings.pinnedNote}</p>
        ) : null}
      </fieldset>
      <PillGroup
        label={strings.view}
        name="mcq-view"
        onChange={(view) => onChange({ view })}
        options={[
          { value: "print", label: strings.viewPrint },
          { value: "online", label: strings.viewOnline },
        ]}
        value={settings.view}
      />
    </>
  );
}

export default function MultipleChoiceQuizMakerWidget() {
  const strings = useToolUi(MULTIPLE_CHOICE_QUIZ_MAKER_STRINGS);
  const locale = useToolLocale();
  const [input, setInput] = useState("");
  const [settings, setSettings] = useState<Settings>({
    title: "",
    instructions: "",
    nameLine: true,
    mixOptions: false,
    mixQuestions: false,
    view: "print",
  });
  const [shuffled, setShuffled] = useState<QuizShuffle | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [printTarget, setPrintTarget] = useState<PrintTarget>("quiz");

  const { questions, errors } = parseQuiz(input);
  const current = shuffled?.text === input ? shuffled : null;
  const shown = arrangeQuiz(
    questions,
    current,
    settings.mixQuestions,
    settings.mixOptions
  );
  const heading = settings.title.trim() || strings.defaultTitle;
  const intro = settings.instructions.trim() || strings.defaultInstructions;
  const mixing = settings.mixOptions || settings.mixQuestions;
  const online = settings.view === "online";

  function update(patch: Partial<Settings>) {
    setSettings((previous) => ({ ...previous, ...patch }));
  }

  function reshuffle() {
    setShuffled(shuffleQuiz(input, questions, secureRandom));
  }

  function mix(patch: Partial<Settings>, on: boolean) {
    update(patch);
    if (on && current === null) {
      reshuffle();
    }
  }

  function print(target: PrintTarget) {
    flushSync(() => {
      setPrintTarget(target);
      update({ view: "print" });
    });
    window.print();
  }

  const sheet = "space-y-6 text-foreground print:text-black";
  const forKey = printTarget === "key";
  const ready = shown.length > 0;

  return (
    <div className="space-y-6">
      <div className="space-y-4 print:hidden">
        <TextAreaField
          hint={
            <>
              {strings.quizHint}{" "}
              {tPlural(strings.questionCount, locale, questions.length, {
                count: questions.length,
              })}
            </>
          }
          id="mcq-input"
          label={strings.quizLabel}
          onChange={setInput}
          placeholder={strings.quizPlaceholder}
          prose
          rows={12}
          value={input}
        />
        <button
          className={BUTTON_SMALL}
          onClick={() => setInput(strings.example)}
          type="button"
        >
          {strings.loadExample}
        </button>
        <ParseErrors errors={errors} strings={strings} />
        <SettingsPanel
          onChange={update}
          onMix={mix}
          settings={settings}
          strings={strings}
        />
        {mixing && current === null && ready ? (
          <Notice>{strings.staleShuffle}</Notice>
        ) : null}
        {ready ? (
          <Actions
            canShuffle={mixing}
            copyText={quizAsText(heading, intro, shown)}
            onPrint={print}
            onReshuffle={reshuffle}
            onToggleKey={() => setShowKey((value) => !value)}
            showKey={showKey}
            strings={strings}
          />
        ) : null}
      </div>

      {online && ready ? (
        <SelfCheck
          key={`${input}::${shown.map((item) => item.order.join("")).join("|")}`}
          shown={shown}
          strings={strings}
        />
      ) : null}
      {ready ? (
        <PrintableQuiz
          className={`${sheet} ${online ? "hidden" : ""} ${forKey ? "print:hidden" : "print:block"}`}
          instructions={intro}
          nameLine={settings.nameLine}
          shown={shown}
          strings={strings}
          title={heading}
        />
      ) : null}
      {ready ? (
        <AnswerKey
          className={`${sheet} ${showKey ? "" : "hidden"} ${forKey ? "print:block" : "print:hidden"}`}
          shown={shown}
          strings={strings}
          title={heading}
        />
      ) : null}
    </div>
  );
}
